import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ActionsObservable, combineEpics, StateObservable } from 'redux-observable'
import { EMPTY, from, of } from 'rxjs'
import { catchError, delayWhen, filter, ignoreElements, map, mergeMap } from 'rxjs/operators'
import { doc, firestore, runTransaction, serverTimestamp, updateDoc } from '~providers/firebase'
import { ApplicationState } from '~redux'
import ChatService, { initialChatSection } from '~services/chat'
import { ChatTemplate } from '~types'
import urlInfo from '~utils/urlInfo'
import {
  attachAudioCommit,
  attachAudioFulfilled,
  attachAudioReject,
  attachImageCommit,
  attachImageFulfilled,
  attachImageReject,
  attachImageRollback,
  detachAudioCommit,
  detachAudioFulfilled,
  detachImageCommit,
  detachImageFulfilled,
  setChat,
  setImageAnimation,
} from './actions'
import { AlreadyUpdatedError } from './errors'
import { ChatState } from './types'
import { cacheAudio, cacheImage } from '~utils/cache'
import { Platform } from 'react-native'

const initialState: ChatState = {
  selectedObject: -1,
  chat: {
    id: '',
    path: '',
    name: '',
    level: '',
    layout: {
      rows: 1,
      columns: 1,
    },
    sections: [],
    created_at: '',
    updated_at: '',
    author_id: '',
    author_name: '',
    classId: '',
    isAuthor: false,
  },
  imageLoading: [],
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setSelectedObject: (state, action: PayloadAction<number>) => {
      state.selectedObject = action.payload
    },
    detachImageRequest: (state) => {
      state.selectedObject = -1
    },
    setTemplate: (state, action: PayloadAction<ChatTemplate>) => {
      state.template = action.payload
    },
    setChatImageLoading: (
      state,
      action: PayloadAction<{
        chat: { id: string; path: string }
        index: number
        loading: boolean
      }>,
    ) => {
      if (state.chat.id !== action.payload.chat.id) {
        return
      }
      state.imageLoading[action.payload.index] = action.payload.loading
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setChat.fulfilled, (state, action) => {
      state.chat = action.payload

      // If previously selected chat image doesn't exist reset selectedObject
      if (
        state.selectedObject !== -1 &&
        (state?.chat?.sections[state.selectedObject]?.image?.src ?? '') ===
          initialChatSection.image.src
      ) {
        state.selectedObject = -1
      }
    })
    builder.addCase(attachImageFulfilled, (state, action) => {
      state.imageLoading = Array(Object.keys(state.chat.sections).length).fill(false)
    })
    builder.addCase(attachImageReject, (state, action) => {
      state.imageLoading = Array(Object.keys(state.chat.sections).length).fill(false)
    })
    builder.addCase(attachImageRollback, (state, action) => {
      state.imageLoading = Array(Object.keys(state.chat.sections).length).fill(false)
    })
  },
})

const attachAudioEpic = (action$: ActionsObservable<ReturnType<typeof attachAudioCommit>>) =>
  action$.pipe(
    filter(attachAudioCommit.match),
    // Don't do anything for web, cloud functions will convert the file and update the document
    filter((action) => action.meta.extension === '.m4a'),
    mergeMap((action) => {
      const documentRef = doc(firestore, action.meta.chat.path)

      return from(
        runTransaction(firestore, async (transaction) => {
          const doc = await transaction.get(documentRef)
          if (typeof doc.exists === 'function' ? !doc.exists() : !doc.exists) {
            throw new Error('Document does not exist')
          }
          const chat = ChatService.chatFromSnapshot(doc)
          const existing = chat.sections[action.meta.index]?.audio[action.meta.type]?.src ?? ''
          const timestampExisting = urlInfo(existing).basename.split('_').pop() ?? 0
          const timestampUpdated = urlInfo(action.payload.url).basename.split('_').pop() ?? 0
          // Latest wins
          if (timestampUpdated < timestampExisting) {
            throw new AlreadyUpdatedError(existing)
          } else {
            transaction.update(documentRef, {
              [`sections.${action.meta.index}.audio.${action.meta.type}.src`]: action.payload.url,
              updated_at: serverTimestamp(),
            })

            return action.payload.url
          }
        }),
      ).pipe(
        delayWhen(() =>
          from(
            Platform.OS !== 'web'
              ? cacheAudio(action.meta.local, action.payload.url)
              : Promise.resolve(),
          ),
        ),
        map(() =>
          attachAudioFulfilled(
            action.meta.chat,
            action.meta.index,
            action.meta.local,
            action.payload.url,
            action.meta.type,
          ),
        ),
        catchError((error) => {
          if (error instanceof AlreadyUpdatedError) {
            const {
              payload: { path, filename },
              meta: { chat, index, type },
            } = action
            return of(
              attachAudioReject({
                chat,
                index,
                remote: error.value, // TODO: Should be handled by the chat subscription
                type,
                path,
                filename,
              }),
            )
          }
          return EMPTY
        }),
      )
    }),
  )

const detachAudioEpic = (action$: ActionsObservable<ReturnType<typeof detachAudioCommit>>) =>
  action$.pipe(
    filter(detachAudioCommit.match),
    mergeMap((action) =>
      from(
        updateDoc(doc(firestore, action.payload.chat.path), {
          [`sections.${action.payload.index}.audio.${action.payload.type}.src`]: '',
          updated_at: serverTimestamp(),
        }),
      ).pipe(map(() => detachAudioFulfilled())),
    ),
  )

const attachImageEpic = (action$: ActionsObservable<ReturnType<typeof attachImageCommit>>) =>
  action$.pipe(
    filter(attachImageCommit.match),
    mergeMap((action) => {
      const documentRef = doc(firestore, action.meta.chat.path)

      return from(
        runTransaction(firestore, async (transaction) => {
          const doc = await transaction.get(documentRef)
          if (typeof doc.exists === 'function' ? !doc.exists() : !doc.exists) {
            throw new Error('Document does not exist')
          }
          const chat = ChatService.chatFromSnapshot(doc)
          const existing = chat.sections[action.meta.index]?.image?.src ?? ''
          const timestampExisting = urlInfo(existing).basename.split('_').pop() ?? 0
          const timestampUpdated = urlInfo(action.payload.url).basename.split('_').pop() ?? 0
          // Latest wins
          if (timestampUpdated < timestampExisting) {
            throw new AlreadyUpdatedError(existing)
          } else {
            transaction.update(documentRef, {
              [`sections.${action.meta.index}.image.src`]: action.payload.url,
              updated_at: serverTimestamp(),
            })

            return action.payload.url
          }
        }),
      ).pipe(
        delayWhen(() =>
          from(
            Platform.OS !== 'web'
              ? cacheImage(action.meta.local, action.payload.url)
              : Promise.resolve(),
          ),
        ),
        map(() =>
          attachImageFulfilled(
            action.meta.chat,
            action.meta.index,
            action.meta.local,
            action.payload.url,
          ),
        ),
        catchError((error) => {
          if (error instanceof AlreadyUpdatedError) {
            return of(
              attachImageReject({
                chat: action.meta.chat,
                index: action.meta.index,
                remote: error.value, // TODO: Should be handled by the chat subscription
                path: action.payload.path,
                filename: action.payload.filename,
              }),
            )
          }
          return EMPTY
        }),
      )
    }),
  )

const detachImageEpic = (action$: ActionsObservable<ReturnType<typeof detachImageCommit>>) =>
  action$.pipe(
    filter(detachImageCommit.match),
    mergeMap((action) =>
      from(
        updateDoc(doc(firestore, action.payload.chat.path), {
          [`sections.${action.payload.index}.image`]: initialChatSection.image,
          updated_at: serverTimestamp(),
        }),
      ).pipe(map(() => detachImageFulfilled())),
    ),
  )

// TODO: Type this and move it to the thunk, pending can do the local state update
const setImageAnimationEpic = (
  action$: ActionsObservable<ReturnType<typeof setImageAnimation>>,
  state$: StateObservable<ApplicationState>,
) =>
  action$.pipe(
    filter(setImageAnimation.match),
    map((action) => {
      const updates: Record<string, any> = {}

      if (action.payload.scale !== undefined) {
        updates[`sections.${action.payload.index}.image.scale`] = action.payload.scale
      }
      if (action.payload.translateX !== undefined) {
        updates[`sections.${action.payload.index}.image.translateX`] = action.payload.translateX
      }
      if (action.payload.translateY !== undefined) {
        updates[`sections.${action.payload.index}.image.translateY`] = action.payload.translateY
      }

      if (Object.keys(updates).length > 0) {
        updates[`updated_at`] = serverTimestamp()
      }

      return {
        path: action.payload.path,
        updates,
      }
    }),
    filter((action) => Object.keys(action.updates).length > 0),
    map(({ path, updates }) => from(updateDoc(doc(firestore, path), updates))),
    ignoreElements(),
  )

const epic = combineEpics(
  attachAudioEpic,
  detachAudioEpic,
  attachImageEpic,
  detachImageEpic,
  setImageAnimationEpic,
)

export { epic as chatEpic }

export const { setSelectedObject, detachImageRequest, setTemplate, setChatImageLoading } =
  chatSlice.actions

export default chatSlice.reducer
